import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import ImmutablePropTypes from 'react-immutable-proptypes'
import first from 'lodash/first'
import pick from 'lodash/pick'
import isFunction from 'lodash/isFunction'
import { connect } from 'react-redux'
import { Map } from 'immutable'
import { bindActionCreators } from 'redux'
import { createImage as createImageAction } from 'services/images/redux/actions'
import { getApplicationDataByKey, getViewRecordsAsList } from 'services/data'
import {
  setDataApplicationByKey as setDataApplicationByKeyAction,
} from 'services/data/redux/actions'
import { STORE_KEY_IMAGES } from 'services/images/store-keys'
import './index.scss'

export const IMAGE_UPLOAD_DATA_APPLICATION_KEY = 'upload'
export const DEFAULT_WORKSPACE_ID = 'workspace'

/**
 * Get the component state by combining all redux state
 * @param {Object} props The props
 * @param {Map} props.data The redux data state
 * @returns The entire component state using props and combined redux state
 */
function getState (props) {
  const { data, storeKey, applicationKey } = props
  const applicationData = getApplicationDataByKey({ data, storeKey, applicationKey }) || Map()
  // Map content and content relationships onto images
  const options = Map({ id: applicationData.get('id') })
  return {
    applicationData,
    record: getViewRecordsAsList({
      data,
      storeKey: STORE_KEY_IMAGES,
      options,
    }).first(),
  }
}
/**
 * Component to allow upload of a single image
 * @param {FileList} files A file list
 * @param {Object} props The props
 * @param {String} props.storeKey The images storeKey
 * @param {Map} props.contentRelationship The image content relationship data
 * @param {Map} props.data The data store
 * @param {String} props.applicationKey The application key
 * @param {String} props.workspaceId The workspace id
 * @param {Function} props.setDataApplication A set data application function
 * @param {String} props.currentImage The current image
 * @param {Function} props.publishCallback A publish callback
 * @param {typeof createImageAction} props.createImage Dispatch a create image action
 * @param {typeof setDataApplicationByKeyAction} props.setDataApplicationByKey
 *   Dispatch set data application by key
 */
async function uploadFiles (files, props) {
  const {
    storeKey,
    createImage,
    setDataApplicationByKey,
    contentRelationship,
    workspaceId,
    applicationKey,
  } = props
  const file = first(files)
  if (!file) {
    return
  }
  let { applicationData } = getState(props)
  const { name } = file
  applicationData = applicationData.mergeIn([workspaceId], {
    data: Map({ originalFileName: name, url: undefined, contentRelationship }),
    compressing: true,
    creating: false,
  })
  setDataApplicationByKey({
    storeKey,
    applicationData,
    applicationKey,
  })
  const fileReader = new FileReader()
  fileReader.onload = (fileEvent) => {
    const { target: { result: dataUri } } = fileEvent
    const { name: originalFileName } = file
    applicationData = applicationData.mergeIn([workspaceId], {
      data: Map({ originalFileName, url: dataUri, contentRelationship }),
      compressing: false,
      creating: false,
    })
    setDataApplicationByKey({
      storeKey,
      applicationData,
      applicationKey: IMAGE_UPLOAD_DATA_APPLICATION_KEY,
    })
    createImage({
      storeKey,
      imageApplicationData: applicationData.get(workspaceId),
      imageApplicationKey: workspaceId,
      applicationKey,
    })
  }
  applicationData = applicationData.mergeIn([workspaceId], {
    compressing: false,
    creating: true,
  })
  setDataApplicationByKey({
    storeKey,
    applicationData,
    applicationKey: IMAGE_UPLOAD_DATA_APPLICATION_KEY,
  })
  fileReader.readAsDataURL(file)
}

/**
 * Handle the on drop event
 * @param {Object} e The on drop event
 * @param {Object} props The component props
 */
async function onDrop (e, props) {
  e.preventDefault()
  uploadFiles(e.dataTransfer.files, props)
}

/**
 * Handle the on drop over event
 * @param {Object} e The on drag over event
 */
async function onDragOver (e) {
  e.preventDefault()
}

/**
 * Component to allow upload of a single image
 * @param {Object} props The props
 * @param {String} props.storeKey The images storeKey
 * @param {Map} props.contentRelationship The image content relationship data
 * @param {Map} props.data The data store
 * @param {String} props.applicationKey The application key
 * @param {String} props.workspaceId The workspace id
 * @param {Function} props.setDataApplication A set data application function
 * @param {String} props.currentImage The current image
 * @param {Function} [props.onCreateImage] Option callback when an image is created
 * @param {typeof createImageAction} props.createImage Dispatch a create image action
 * @param {typeof setDataApplicationByKeyAction} props.setDataApplicationByKey
 *   Dispatch set data application by key
 */
function ImageUpload (props) {
  const {
    storeKey, setDataApplicationByKey, applicationKey, workspaceId, onCreateImage,
  } = props
  const { applicationData } = getState(props)
  const url = applicationData.getIn([workspaceId, 'data', 'url'])
  const id = applicationData.getIn([workspaceId, 'data', 'id'])
  const compressing = applicationData.getIn([workspaceId, 'compressing'])
  const creating = applicationData.get([workspaceId, 'creating'])
  useEffect(() => () => {
    setDataApplicationByKey({
      storeKey,
      applicationData: applicationData.delete(workspaceId),
      applicationKey,
    })
  }, [])
  useEffect(() => {
    if (isFunction(onCreateImage)) {
      onCreateImage(applicationData.getIn([workspaceId, 'data']))
    }
  }, [id])
  return (
    <div className="image-upload" onDrop={(e) => onDrop(e, props)} onDragOver={onDragOver}>
      { url && <img className="image-upload__image" src={url} title={`image-upload-application-key-${applicationKey}-${workspaceId}`} /> }
      { !url && compressing && <p className="image-upload__text">Compressing image...</p> }
      { !url && creating && <p className="image-upload__text">Creating image...</p> }
      { !url && !compressing && !creating && <p className="image-upload__text-instructions">Drag Image Here</p> }
    </div>
  )
}

ImageUpload.propTypes = {
  storeKey: PropTypes.string.isRequired,
  contentRelationship: ImmutablePropTypes.map,
  data: ImmutablePropTypes.map.isRequired,
  workspaceId: PropTypes.string,
  createImage: PropTypes.func.isRequired,
  setDataApplicationByKey: PropTypes.func.isRequired,
  currentImage: PropTypes.string,
  publishCallback: PropTypes.func,
  applicationKey: PropTypes.string,
  onCreateImage: PropTypes.func,
}

ImageUpload.defaultProps = {
  storeKey: STORE_KEY_IMAGES,
  workspaceId: DEFAULT_WORKSPACE_ID,
  applicationKey: IMAGE_UPLOAD_DATA_APPLICATION_KEY,
}

export default connect(
  (state) => pick(state, ['data']),
  (dispatch) => bindActionCreators({
    createImage: createImageAction,
    setDataApplicationByKey: setDataApplicationByKeyAction,
  }, dispatch),
)(ImageUpload)
